import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import loginSSOService, { getProfileService, loginService } from 'services/authenticate';
import { LoginParamsTypes, LoginSSOParamsTypes, UserInfoTypes } from 'services/authenticate/types';
import {
  removeAccessToken, removeLocalStorage, setAccessToken, setLocalStorage,
} from 'services/common/storage';
import { LOCAL_STORAGE } from 'utils/constants';

interface UserState {
  data?: UserInfoTypes;
  loading: boolean;
}

const initialState: UserState = {
  loading: false,
};

export const loginSSOAction = createAsyncThunk<
  UserInfoTypes,
  LoginSSOParamsTypes,
  { rejectValue: ErrorResponse }
>('userReducer/loginSSOAction', async (params, { rejectWithValue }) => {
  try {
    const response = await loginSSOService(params);
    setAccessToken(response.accessToken);
    return response.customer;
  } catch (error) {
    return rejectWithValue(error as ErrorResponse);
  }
});

export const loginAction = createAsyncThunk<
  UserInfoTypes,
  LoginParamsTypes,
  { rejectValue: ErrorResponse }
>('userReducer/loginAction', async (params, { rejectWithValue }) => {
  try {
    const response = await loginService(params);
    setAccessToken(response.accessToken);
    return response.customer;
  } catch (error) {
    return rejectWithValue(error as ErrorResponse);
  }
});

export const getProfileAction = createAsyncThunk<
  UserInfoTypes,
  void,
  { rejectValue: ErrorResponse[] }
>('userReducer/getProfileAction', async (_, { rejectWithValue }) => {
  try {
    return await getProfileService();
  } catch (error) {
    const err = error as any;
    if (err && err.response.data.errors.length && err.response.data.errors[0].code === 401) {
      removeAccessToken();
    }
    return rejectWithValue(err);
  }
});

export const UserSlice = createSlice({
  name: 'userReducer',
  initialState,
  reducers: {
    logoutAction: ($state) => {
      $state.data = undefined;
      removeLocalStorage(LOCAL_STORAGE.X_IS_SSO);
    },
  },
  extraReducers(builder) {
    builder.addCase(loginSSOAction.pending, ($state) => {
      $state.loading = true;
    });
    builder.addCase(loginSSOAction.fulfilled, ($state, action) => {
      $state.data = action.payload;
      $state.loading = false;
      setLocalStorage(LOCAL_STORAGE.X_IS_SSO, 'true');
    });
    builder.addCase(loginSSOAction.rejected, ($state) => {
      $state.loading = false;
    });
    builder.addCase(loginAction.pending, ($state) => {
      $state.loading = true;
    });
    builder.addCase(loginAction.fulfilled, ($state, action) => {
      $state.data = action.payload;
      $state.loading = false;
      setLocalStorage(LOCAL_STORAGE.X_IS_SSO, 'false');
    });
    builder.addCase(loginAction.rejected, ($state) => {
      $state.loading = false;
    });
    builder.addCase(getProfileAction.fulfilled, ($state, action) => {
      $state.data = action.payload;
    });
  },
});

export const { logoutAction } = UserSlice.actions;

export default UserSlice.reducer;
